import 'dayjs/locale/pt-br';
import 'dayjs/locale/es';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isToday from 'dayjs/plugin/isToday';
import relativeTime from 'dayjs/plugin/relativeTime';
import i18n from 'i18next';

import {
  NotificationObjectData,
  NotificationWebItem,
} from './notifications.model';

dayjs.extend(customParseFormat);
dayjs.extend(isToday);
dayjs.extend(relativeTime);

const BUDGET_NOTIFICATION_TYPE_REGEX =
  /^NOTIFY_(?:(FIELD|MONTH|ITEM_GROUP)_)?(\d+)$/;

interface NotificationContent {
  title: string;
  description: string | null;
}

const isBudgetNotificationType = (type: string): boolean =>
  BUDGET_NOTIFICATION_TYPE_REGEX.test(type);

const getDayjsLocale = () => {
  const lang = i18n.language;
  if (lang?.startsWith('pt')) return 'pt-br';
  if (lang?.startsWith('es')) return 'es';
  return 'en';
};

const parseObjectData = (objectData: string): NotificationObjectData | null => {
  try {
    return JSON.parse(objectData);
  } catch {
    return null;
  }
};

const formatNotificationTime = (createdAt: string): string => {
  const date = dayjs(createdAt);
  const locale = getDayjsLocale();
  const time = date.format('HH:mm');

  if (date.isToday()) {
    return `${i18n.t('notification_web.today')} • ${time}`;
  }

  const distance = date.locale(locale).fromNow();
  return `${distance} • ${time}`;
};

const localizeMonthName = (monthName: string | undefined): string => {
  if (!monthName) return '';
  const parsed = dayjs(monthName, 'MMMM', 'en', true);
  if (!parsed.isValid()) return monthName;
  return parsed.locale(getDayjsLocale()).format('MMMM');
};

const getBudgetNotificationContent = (
  type: string,
  data: NotificationObjectData,
): NotificationContent | null => {
  const match = BUDGET_NOTIFICATION_TYPE_REGEX.exec(type);
  if (!match) return null;

  const variant = match[1];
  const percentage = Number(match[2]);

  switch (variant) {
    case 'FIELD':
      return {
        title: i18n.t('notification_web.field_budget_title', { percentage }),
        description: i18n.t('notification_web.field_budget_description', {
          label: data.label,
          percentage,
        }),
      };
    case 'MONTH': {
      const monthName = localizeMonthName(data.monthName);
      return {
        title: i18n.t('notification_web.month_budget_title', {
          monthName,
          percentage,
        }),
        description: i18n.t('notification_web.month_budget_description', {
          farm: data.farm,
          monthName,
          percentage,
        }),
      };
    }
    case 'ITEM_GROUP':
      return {
        title: i18n.t('notification_web.item_group_budget_title', {
          itemGroup: data.itemGroup,
          percentage,
        }),
        description: i18n.t('notification_web.item_group_budget_description', {
          farm: data.farm,
          itemGroup: data.itemGroup,
          percentage,
        }),
      };
    default:
      return {
        title: i18n.t('notification_web.season_budget_title', {
          seasonName: data.seasonName,
          percentage,
        }),
        description: i18n.t('notification_web.season_budget_description', {
          farm: data.farm,
          percentage,
        }),
      };
  }
};

const getNotificationContent = (
  type: string,
  data: NotificationObjectData | null,
  fallbackMessage: string,
): NotificationContent => {
  if (data) {
    const budgetContent = getBudgetNotificationContent(type, data);
    if (budgetContent) return budgetContent;

    if (data.itemName) {
      return {
        title: i18n.t('notification_web.item_stock_low_title', {
          itemName: data.itemName,
        }),
        description: i18n.t('notification_web.item_stock_low_description', {
          actualQty: data.actualQty,
          itemStockLowValue: data.itemStockLowValue,
          stockUom: data.stockUom,
        }),
      };
    }
  }

  return { title: fallbackMessage, description: null };
};

const getNotificationTitle = (item: NotificationWebItem): string =>
  getNotificationContent(
    item.type,
    parseObjectData(item.objectData),
    item.message,
  ).title;

const getNotificationDescription = (item: NotificationWebItem): string | null =>
  getNotificationContent(
    item.type,
    parseObjectData(item.objectData),
    item.message,
  ).description;

export {
  formatNotificationTime,
  getNotificationContent,
  getNotificationDescription,
  getNotificationTitle,
  isBudgetNotificationType,
  parseObjectData,
};
